import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { DialogTitleWithBug } from "@/components/ui/dialog-title-with-bug";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ImagePlus, X } from "lucide-react";
import { useGrowthSquad } from "@/hooks/useGrowthSquad";
import { useGrowthSquadMembers2Y3X } from "@/hooks/useGrowthSquadMembers2Y3X";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useBrandManager } from "@/hooks/useBrandManager";
import { useAuth } from "@/hooks/useAuth";
import { PhaseSelector } from "@/components/scale-2y3x/PhaseSelector";
import { useCreateTaskDialog } from "@/contexts/CreateTaskDialogContext";
import { useLocation } from "react-router-dom";

export const GlobalCreateTaskDialog = () => {
  const location = useLocation();
  const { isOpen, dialogData, closeCreateTaskDialog, onCreateTask, isLoading, setIsLoading } = useCreateTaskDialog();
  const { user } = useAuth();
  const { currentBrandId } = useBrandManager();

  const [taskName, setTaskName] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [phaseId, setPhaseId] = useState<string>("");
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  // Use the appropriate squad members hook based on flag
  const { squadMembers: regularSquadMembers } = useGrowthSquad();
  const { members: y3xMembers } = useGrowthSquadMembers2Y3X({ typeFilter: "2Y3X Internal Team" });

  // Determine which members to use based on dialogData
  const use2Y3XMembers = dialogData?.use2Y3XMembers || false;
  const squadMembers = use2Y3XMembers
    ? y3xMembers.map(m => ({ id: m.id, name: m.name, alias: m.alias, email: m.email }))
    : regularSquadMembers;

  // Pre-fill with idea data when dialog opens
  useEffect(() => {
    if (isOpen && dialogData) {
      setTaskName(dialogData.ideaName);
      setTaskDescription(dialogData.ideaDescription);
    }
  }, [isOpen, dialogData]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length + selectedImages.length > 5) {
      toast({
        title: "Too many images",
        description: "You can only upload up to 5 images per task.",
        variant: "destructive"
      });
      return;
    }
    setSelectedImages(prev => [...prev, ...files]);
  };

  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const uploadImages = async (): Promise<string[]> => {
    if (selectedImages.length === 0) return [];

    if (!currentBrandId) {
      toast({
        title: "Upload Error",
        description: "No brand selected for image upload.",
        variant: "destructive"
      });
      return [];
    }

    setUploadingImages(true);
    const uploadedUrls: string[] = [];

    try {
      for (const image of selectedImages) {
        const sanitizedName = image.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const fileName = `${Date.now()}_${sanitizedName}`;
        const filePath = `${currentBrandId}/${fileName}`;

        const { data, error: uploadError } = await supabase.storage
          .from('task-images')
          .upload(filePath, image);

        if (uploadError) {
          toast({
            title: "Upload Error",
            description: `Failed to upload ${image.name}: ${uploadError.message}`,
            variant: "destructive"
          });
          continue;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('task-images')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
      }

      if (uploadedUrls.length > 0) {
        toast({
          title: "Upload Successful",
          description: `${uploadedUrls.length} image(s) uploaded successfully.`,
        });
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      toast({
        title: "Upload Error",
        description: "Failed to upload images. Please try again.",
        variant: "destructive"
      });
    } finally {
      setUploadingImages(false);
    }

    return uploadedUrls;
  };

  const handleSubmit = async () => {
    if (!taskName.trim()) {
      toast({
        title: "Validation Error",
        description: "Task name is required.",
        variant: "destructive"
      });
      return;
    }

    if (!assignedTo) {
      toast({
        title: "Validation Error",
        description: "Please assign the task to a team member.",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsLoading(true);
      const imageUrls = await uploadImages();

      if (onCreateTask) {
        await onCreateTask({
          name: taskName.trim(),
          description: taskDescription.trim(),
          assignedTo: assignedTo,
          images: imageUrls,
          phase_id: phaseId && phaseId !== "none" ? phaseId : undefined,
        });
      }
      handleClose();
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      toast({
        title: "Error",
        description: "Failed to create task. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setTaskName("");
    setTaskDescription("");
    setAssignedTo("");
    setPhaseId("");
    setSelectedImages([]);
    closeCreateTaskDialog();
  };

  if (!dialogData) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md h-[80vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitleWithBug
            popupName="Create Task"
            popupContext={location.pathname}
            isOpen={isOpen}
          >
            Create Task
          </DialogTitleWithBug>
          <DialogDescription>
            Create a new task from the selected idea and assign it to a squad member.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 h-0">
          <div className="space-y-4 px-4 py-1">
            <div className="space-y-2">
              <Label htmlFor="task-name">Task Name</Label>
              <Input
                id="task-name"
                placeholder="Enter task name"
                value={taskName}
                onChange={(e) => setTaskName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="task-description">Description</Label>
              <Textarea
                id="task-description"
                placeholder="Describe what needs to be done..."
                value={taskDescription}
                onChange={(e) => setTaskDescription(e.target.value)}
                className="min-h-24"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="assigned-to">Assign To</Label>
              <Select value={assignedTo} onValueChange={setAssignedTo}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a squad member" />
                </SelectTrigger>
                <SelectContent>
                  {squadMembers.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name} ({member.alias})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {use2Y3XMembers && (
              <div className="space-y-2">
                <Label htmlFor="phase">Phase (Optional)</Label>
                <PhaseSelector
                  value={phaseId}
                  onValueChange={setPhaseId}
                  brandId={currentBrandId || undefined}
                  methodology="2y3x"
                  placeholder="Select a phase"
                  allowNone={true}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="task-images">Task Images</Label>
              <div className="space-y-2">
                <Input
                  id="task-images"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('task-images')?.click()}
                  className="w-full"
                  disabled={selectedImages.length >= 5}
                >
                  <ImagePlus className="w-4 h-4 mr-2" />
                  Add Images ({selectedImages.length}/5)
                </Button>

                {selectedImages.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {selectedImages.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={URL.createObjectURL(image)}
                          alt={`Upload ${index + 1}`}
                          className="w-full h-20 object-cover rounded border"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => removeImage(index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="flex-shrink-0 flex gap-2 mt-4">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || uploadingImages || !taskName.trim() || !assignedTo}
          >
            {isLoading || uploadingImages ? (uploadingImages ? "Uploading..." : "Creating...") : "Create Task"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
